<template>
    <span>
        <!-- Trigger -->
        <button type="button"
                class="relative inline-flex w-fit items-center justify-center gap-2 rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-blue-700 hover:border-transparent hover:bg-blue-100 hover:text-blue-700"
                @click="open"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 13a4 4 0 1 0 0 -8a4 4 0 0 0 0 8"/><path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1"/><path d="M17 10h2a2 2 0 0 1 2 2v1"/><path d="M15 5a3 3 0 0 1 0 6"/></svg>
            Show Admins
        </button>

        <slide-over-panel v-model:show="show" width="max-w-xl">
            <template #title>Admins for {{ userName }}</template>
            <template #title-content>
                Managers, admins, and super admins who share a group with this user.
            </template>
            <template #content>
                <!-- Loading -->
                <div v-if="loading" class="py-6 text-sm text-gray-500">
                    Loading admins&hellip;
                </div>

                <!-- Error -->
                <div v-else-if="loadError" class="py-6 text-sm text-red-600">
                    Unable to load admins. Please try again.
                </div>

                <!-- Empty -->
                <div v-else-if="groups.length === 0" class="py-6 text-sm text-gray-500">
                    No admins found for this user.
                </div>

                <!-- Grouped list -->
                <div v-else class="flex flex-col gap-6 pb-10">
                    <!-- Fallback disclaimer -->
                    <div v-if="isFallback" class="relative overflow-clip rounded bg-yellow-50 p-4">
                        <span class="absolute left-0 top-0 h-full w-1 bg-yellow-200"></span>
                        <p class="mb-0 text-sm text-yellow-800">
                            This user doesn't share a group with any admin, so every admin on the site is
                            shown below.
                        </p>
                    </div>

                    <div v-for="group in groups" :key="group.name">
                        <div class="mb-2 flex items-center justify-between border-b border-gray-200 pb-1">
                            <h4 class="text-sm font-semibold text-gray-900">
                                {{ group.display_name }}
                                <span class="ml-1 text-xs font-normal text-gray-500">({{ group.count }})</span>
                            </h4>
                            <button type="button"
                                    class="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                                    @click="copyLevelEmails(group)"
                            >
                                <span v-if="copiedLevel === group.name" class="text-green-600">Copied!</span>
                                <span v-else>Copy emails</span>
                            </button>
                        </div>
                        <ul class="flex flex-col divide-y divide-gray-100">
                            <li v-for="admin in group.admins"
                                :key="admin.email"
                                class="flex flex-col py-2"
                            >
                                <span class="text-sm font-medium text-gray-900">{{ admin.name }}</span>
                                <a :href="`mailto:${admin.email}`" class="text-sm text-gray-500 hover:text-blue-600">{{ admin.email }}</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </slide-over-panel>
    </span>
</template>

<script>
import axios from "axios"
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue"

export default {
    name: "UserAdminsSlideOver",
    components: {SlideOverPanel},
    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
        companyId: {
            type: String,
            required: true,
        },
        userName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            show: false,
            loading: false,
            loadError: false,
            hasLoaded: false,
            groups: [],
            isFallback: false,
            copiedLevel: null,
            copiedLevelTimeout: null,
        }
    },
    methods: {
        open() {
            this.show = true
            if (!this.hasLoaded) {
                this.fetchAdmins()
            }
        },
        fetchAdmins() {
            this.loading = true
            this.loadError = false
            axios.get(`/api-internal/${this.companyId}/users/${this.userId}/admins`)
                .then(response => {
                    this.groups = response.data && response.data.data ? response.data.data : []
                    this.isFallback = !!(response.data && response.data.is_fallback)
                    this.hasLoaded = true
                })
                .catch(error => {
                    console.log(error)
                    this.loadError = true
                })
                .then(() => {
                    this.loading = false
                })
        },
        copyLevelEmails(group) {
            const text = (group.admins || []).map(admin => admin.email).join(', ')
            this.copyToClipboard(text).then(() => {
                this.copiedLevel = group.name
                if (this.copiedLevelTimeout) clearTimeout(this.copiedLevelTimeout)
                this.copiedLevelTimeout = setTimeout(() => {
                    this.copiedLevel = null
                }, 1200)
            }).catch(() => {
                alert('Unable to copy emails to your clipboard.')
            })
        },
        copyToClipboard(text) {
            // Prefer the async Clipboard API (available over https/ngrok); fall back to a temporary
            // textarea + execCommand for insecure contexts (e.g. http://localhost).
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(text)
            }
            return new Promise((resolve, reject) => {
                try {
                    const textarea = document.createElement('textarea')
                    textarea.value = text
                    textarea.style.position = 'fixed'
                    textarea.style.opacity = '0'
                    document.body.appendChild(textarea)
                    textarea.focus()
                    textarea.select()
                    const ok = document.execCommand('copy')
                    document.body.removeChild(textarea)
                    ok ? resolve() : reject()
                } catch (e) {
                    reject(e)
                }
            })
        },
    },
}
</script>
